import { useTranslation } from "react-i18next";
import { MdImage } from "react-icons/md";
import { ScenariaPlan } from "src/model/model";
import { IMedia } from "src/model/unityObject";

interface DecorStatusRowProps {
  plan?: ScenariaPlan;
  /** Hydrated conversation media — carries the s3 urls for the thumbnail. */
  contextImages: IMedia[];
  /** Disables every action while a decor-change request is in flight. */
  busy?: boolean;
  /** "Baser sur une de vos images" / "Changer" — opens the reference picker.
   * Omit (with the other handlers) to render the row informative-only. */
  onPick?: () => void;
  onRemove?: (media: IMedia) => void;
  /** Opens the full-size viewer (Options provenance modal). */
  onView?: () => void;
  onRetryAnalysis?: () => void;
  isRetryingAnalysis?: boolean;
  /** Picked-but-not-integrated image. When set it takes over the row — the
   * pick action just happened HERE ("Baser sur une de vos images"), so the row
   * must visibly answer it instead of still claiming an AI-invented decor. */
  stagedMedia?: IMedia | null;
  onIntegrateStaged?: () => void;
  className?: string;
}

const ACTION_LINK = "text-purple-500 hover:text-purple-600 font-medium text-xs disabled:opacity-50";

// The affirmative decor-provenance statement: always answers "is my image
// driving the decor?" in reserved space, in one of three server-truth states —
// none (LLM-invented), integrated (thumbnail + filename), or vision-failed
// (decor composed without the image + retry). Staged-but-unapplied is NOT a
// state here: that is StagedReferenceImageCard's job — this row never shows
// anything the server hasn't persisted.
const DecorStatusRow = ({
  plan,
  contextImages,
  busy = false,
  onPick,
  onRemove,
  onView,
  onRetryAnalysis,
  isRetryingAnalysis = false,
  stagedMedia = null,
  onIntegrateStaged,
  className = "",
}: DecorStatusRowProps) => {
  const { t } = useTranslation();
  const pc = "pages.chat";

  // Max 1 reference image by design (400-char trunk budget) — entry [0] is it.
  const entry = (plan?.attachedImages ?? [])[0];
  const media = entry
    ? contextImages.find((m) => Number(m.id) === Number(entry.mediaId))
    : undefined;
  const name = media?.filename || media?.name || t(`${pc}.decorRowYourImage`);

  // Staged variant: instant feedback for the pick that just happened. The
  // dashed thumbnail border marks "pending" (vs the solid border of an
  // integrated image); the integrate link mirrors the banner's CTA.
  if (stagedMedia) {
    const stagedName = stagedMedia.filename || stagedMedia.name;
    return (
      <div className={`flex flex-wrap items-center gap-x-2.5 gap-y-1 ${className}`}>
        <img
          src={stagedMedia.thumbnail_s3_url || stagedMedia.s3_url}
          alt={stagedName}
          className="h-8 w-8 rounded object-cover border border-dashed border-purple-300 shrink-0"
        />
        <span className="text-sm text-gray-700 min-w-0">
          {t(`${pc}.decorRowStagedPrefix`)}{" "}
          <span className="font-medium text-purple-800 break-all">{stagedName}</span>
          <span className="text-purple-600"> — {t(`${pc}.decorRowStagedSuffix`)}</span>
        </span>
        {onIntegrateStaged && (
          <button type="button" className={ACTION_LINK} disabled={busy} onClick={onIntegrateStaged}>
            {t(`${pc}.stagedReferenceImageIntegrate`)}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={`flex flex-wrap items-center gap-x-2.5 gap-y-1 ${className}`}>
      {entry && media ? (
        <img
          src={media.thumbnail_s3_url || media.s3_url}
          alt={name}
          className={`h-8 w-8 rounded object-cover border border-purple-200 shrink-0 ${
            onView ? "cursor-pointer" : ""
          }`}
          onClick={onView}
        />
      ) : (
        <span className="h-8 w-8 rounded border border-dashed border-gray-300 flex items-center justify-center text-gray-400 shrink-0">
          <MdImage size={14} />
        </span>
      )}

      {entry ? (
        <span className="text-sm text-gray-700 min-w-0">
          {t(`${pc}.decorRowIntegrated`)}{" "}
          <span className="font-medium text-purple-800 break-all">{name}</span>
        </span>
      ) : (
        <span className="text-sm text-gray-600">{t(`${pc}.decorRowNone`)}</span>
      )}

      {/* Vision failed: the image is linked but its description could not be
          produced — the decor was composed WITHOUT it. Never silent. */}
      {entry?.failed && (
        <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
          {t(`${pc}.decorRowAnalysisFailed`)}
        </span>
      )}

      <span className="flex items-center gap-2 shrink-0">
        {entry?.failed && onRetryAnalysis && (
          <button
            type="button"
            className={ACTION_LINK}
            disabled={busy || isRetryingAnalysis}
            onClick={onRetryAnalysis}
          >
            {isRetryingAnalysis
              ? t("pages.chat.configureGeneration.imageAnalysisRetrying")
              : t("pages.chat.configureGeneration.imageAnalysisRetry")}
          </button>
        )}
        {entry && onView && (
          <button type="button" className={ACTION_LINK} disabled={busy} onClick={onView}>
            {t("pages.chat.configureGeneration.trunkImageLink")}
          </button>
        )}
        {entry
          ? onPick && (
              <button type="button" className={ACTION_LINK} disabled={busy} onClick={onPick}>
                {t(`${pc}.decorRowChange`)}
              </button>
            )
          : onPick && (
              <button type="button" className={ACTION_LINK} disabled={busy} onClick={onPick}>
                {t(`${pc}.decorRowNoneAction`)}
              </button>
            )}
        {entry && media && onRemove && (
          <button
            type="button"
            className={ACTION_LINK}
            disabled={busy}
            onClick={() => onRemove(media)}
          >
            {t(`${pc}.decorRowRemove`)}
          </button>
        )}
      </span>
    </div>
  );
};

export default DecorStatusRow;
